// src/pages/Admin/AdminUsers.tsx
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { motion } from 'framer-motion';
import { Search, Filter, User, Shield, Ban, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface User {
  _id: string;
  fullName: string;
  email: string;
  phone?: string;
  role: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  rating?: number;
  completedOrders?: number;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, statusFilter, search]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUserManagement({
        page,
        limit,
        search: search || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
      });
      if (res.data.success) {
        setUsers(res.data.data.users);
        setTotalPages(res.data.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId: string, current: boolean) => {
    try {
      const res = await adminAPI.updateUserStatus(userId, { isActive: !current });
      if (res.data.success) {
        toast.success(`User ${!current ? 'activated' : 'deactivated'}`);
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleVerified = async (userId: string, current: boolean) => {
    try {
      const res = await adminAPI.updateUserStatus(userId, { isVerified: !current });
      if (res.data.success) {
        toast.success(`User ${!current ? 'verified' : 'unverified'}`);
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const res = await adminAPI.updateUserStatus(userId, { role: newRole });
      if (res.data.success) {
        toast.success('Role updated');
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-800',
      support: 'bg-blue-100 text-blue-800',
      rider: 'bg-green-100 text-green-800',
      customer: 'bg-gray-100 text-gray-800',
    };
    return colors[role] || 'bg-gray-100';
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">User Management</h1>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="w-40">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="support">Support</option>
              <option value="rider">Rider</option>
              <option value="customer">Customer</option>
            </select>
          </div>
          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">User</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Role</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Orders</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Rating</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Joined</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold">
                            {user.fullName?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <p className="font-medium">{user.fullName}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                            {user.phone && <p className="text-xs text-gray-400">{user.phone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            {user.isActive ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                            <span className="text-xs">{user.isActive ? 'Active' : 'Inactive'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {user.isVerified ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-gray-400" />
                            )}
                            <span className="text-xs">{user.isVerified ? 'Verified' : 'Unverified'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{user.completedOrders || 0}</td>
                      <td className="px-6 py-4">{user.rating?.toFixed(1) || '0.0'}</td>
                      <td className="px-6 py-4 text-sm">{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                            className="px-2 py-1 text-xs border rounded"
                          >
                            <option value="customer">Customer</option>
                            <option value="rider">Rider</option>
                            <option value="support">Support</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={() => handleToggleActive(user._id, user.isActive)}
                            className={`p-2 rounded-lg ${user.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleVerified(user._id, user.isVerified)}
                            className={`p-2 rounded-lg ${user.isVerified ? 'text-gray-400' : 'text-emerald-600'} hover:bg-gray-100`}
                            title={user.isVerified ? 'Unverify' : 'Verify'}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminUsers;